/**
 * Unified tool type definitions shared across all agents.
 */

import {ToolCallStatus} from 'llm/chat-library/progress/ProgressTypes';
import {isNativeLoadSkill} from 'llm/chat-library/tool-details/LoadSkillToolCallParser';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/** A tool icon source is either a named glyph or an image URL with server name for alt text. */
export type ToolIconSource = {iconName: BenchlingIconName} | {iconUrl: string; serverName: string};

export function isGlyphIcon(icon: ToolIconSource): icon is {iconName: BenchlingIconName} {
  return (icon as {iconName?: unknown}).iconName != null;
}

// ============================================================================
// Tool Type Enum
// ============================================================================

/**
 * All tool types used across agents (Deep Research, Data Capture, etc.).
 *
 * When adding tools, also update:
 * - TOOL_DISPLAY_NAMES_IN_PROGRESS / TOOL_DISPLAY_NAMES_COMPLETED
 * - TOOL_ICONS
 * - TOOLS_TO_DISPLAY (if the tool should appear in the progress UI)
 *
 * Keep in sync with backend toolkit definitions in benchling/llm.
 */
export enum ToolType {
  // --- Shared tools (used by multiple agents) ---
  ToolSearch = 'ToolSearch',
  Think = 'Think',
  IdsToModelsTool = 'IdsToModelsTool',
  ReadBenchlingDocumentTool = 'ReadBenchlingDocument',
  SaveArtifact = 'SaveArtifact',
  ListArtifacts = 'ListArtifacts',
  ViewArtifact = 'ViewArtifact',
  LoadFileAsArtifact = 'LoadFileAsArtifact',
  LoadDocumentAttachmentAsArtifact = 'LoadDocumentAttachmentAsArtifact',
  SubstituteInArtifact = 'SubstituteInArtifact',
  UploadFileToSandbox = 'UploadFileToSandbox',
  LoadDropdownOptions = 'LoadDropdownOptions',
  HelpDocs = 'HelpDocs',
  LoadPackageDocs = 'LoadPackageDocs',
  AskWarehouse = 'AskWarehouse',
  WebSearch = 'WebSearch',

  // --- Deep Research: supervisor-level tools ---
  ResearchSubAgent = 'ResearchSubAgent',
  CompleteAnswer = 'CompleteAnswer',
  PublicDataSubAgent = 'PublicDataSubAgent',

  // --- Deep Research: sub-agent tools ---
  CompleteAnswerTool = 'CompleteAnswerTool',
  DeepResearchTool = 'DeepResearchTool',
  SearchTool = 'SearchTool',
  GetWarehouseOverviewTool = 'GetWarehouseOverviewTool',
  SearchNotebookEntries = 'SearchNotebookEntries',
  SearchSequencesTool = 'SearchSequencesTool',
  QueryWarehouseTool = 'QueryWarehouseTool',
  AttachmentTool = 'AttachmentTool',
  PlottingTool = 'PlottingTool',
  GenerateImageTool = 'GenerateImageTool',
  CustomCodeTool = 'CustomCodeTool',
  RunCodeTool = 'RunCodeTool',

  // --- Skills tools ---
  LoadSkill = 'LoadSkill',
  ManageSkill = 'ManageSkill',

  // --- Analysis agent tools ---
  CreateAnalysisPlan = 'CreateAnalysisPlan',

  // --- Data Capture specific tools ---
  Respond = 'Respond',
  ReadTargetDocument = 'ReadTargetDocument',
  ReadDraftDocument = 'ReadDraftDocument',
  AskQuestion = 'AskQuestion',
  InstantiateDocument = 'InstantiateDocument',
  SearchEntriesAndTemplates = 'SearchEntriesAndTemplates',
  SearchBenchlingObjects = 'SearchBenchlingObjects',
  TranscribeFile = 'TranscribeFile',
  ReviewTool = 'ReviewTool',
  EditTableCells = 'EditTableCells',
  UpdateDocument = 'UpdateDocument',
  InsertDocumentContent = 'InsertDocumentContent',
  SearchFiles = 'SearchFiles',
  SearchSchemas = 'SearchSchemas',
  FillTables = 'FillTables',
  ReadFiles = 'ReadFiles',
  CreateLookupConfig = 'CreateLookupConfig',

  // --- Co-scientist tools ---
  ListFolderContents = 'ListFolderContents',
  CreateFolder = 'CreateFolder',
  CreateEntry = 'CreateEntry',
  CreateFile = 'CreateFile',
  UpdateFile = 'UpdateFile',
  ReadFile = 'ReadFile',
  CreateAASequence = 'CreateAASequence',
  CreateMolecule = 'CreateMolecule',
  CreateCustomEntities = 'CreateCustomEntities',
  // Bulk update tool for custom entities (paired with CreateCustomEntities).
  UpdateCustomEntities = 'UpdateCustomEntities',
  ArchiveItem = 'ArchiveItem',
  RenameItem = 'RenameItem',
  SetTargetEntry = 'SetTargetEntry',
  CreateDocumentTool = 'CreateDocumentTool',
  InvokeToolsFromFile = 'InvokeToolsFromFile',
  RegisterEntities = 'RegisterEntities',
  TransferIntoContainers = 'TransferIntoContainers',
  CreatePlate = 'CreatePlate',
  StartChatSession = 'StartChatSession',
  GetChatSession = 'GetChatSession',
  ScheduleForFuture = 'ScheduleForFuture',
  CouncilTool = 'CouncilTool',
  CreateProcedureRunPlan = 'CreateProcedureRunPlan',
  ValidateProcedureRunPlan = 'ValidateProcedureRunPlan',
  PromoteProcedureRunPlan = 'PromoteProcedureRunPlan',
  UpdateProcedureRunPlanConditions = 'UpdateProcedureRunPlanConditions',
  CreateProcedureRunParameterValue = 'CreateProcedureRunParameterValue',
}

export const RESPONSE_TOOLS: Array<ToolType> = [ToolType.CompleteAnswer, ToolType.Respond];

// ============================================================================
// Display Names
// ============================================================================

/**
 * Human-readable display names shown while a tool is running (present tense).
 * Tools that are not displayed in the progress UI have null values.
 */
export const TOOL_DISPLAY_NAMES_IN_PROGRESS: Record<ToolType, string | null> = {
  // Shared
  [ToolType.ToolSearch]: 'Loading tools',
  [ToolType.Think]: 'Thinking',
  [ToolType.IdsToModelsTool]: 'Looking things up',
  [ToolType.ReadBenchlingDocumentTool]: 'Reading notebook',
  [ToolType.SaveArtifact]: 'Saving artifact',
  [ToolType.ListArtifacts]: 'Listing artifacts',
  [ToolType.ViewArtifact]: 'Viewing artifact',
  [ToolType.LoadFileAsArtifact]: 'Loading file as artifact',
  [ToolType.LoadDocumentAttachmentAsArtifact]: 'Loading attachment as artifact',
  [ToolType.SubstituteInArtifact]: 'Editing artifact',
  [ToolType.UploadFileToSandbox]: 'Uploading file to sandbox',
  [ToolType.LoadDropdownOptions]: 'Loading dropdown options',
  [ToolType.HelpDocs]: 'Searching help docs',
  [ToolType.LoadPackageDocs]: 'Loading package docs',
  [ToolType.AskWarehouse]: 'Asking warehouse',
  [ToolType.WebSearch]: 'Searching the web',

  // Deep Research
  [ToolType.ResearchSubAgent]: 'Starting research subagent',
  [ToolType.CompleteAnswer]: 'Generating citations',
  [ToolType.CompleteAnswerTool]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.DeepResearchTool]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.PublicDataSubAgent]: 'Searching public data',
  [ToolType.SearchTool]: 'Searching Benchling',
  [ToolType.GetWarehouseOverviewTool]: 'Getting warehouse overview',
  [ToolType.SearchNotebookEntries]: 'Searching notebook entries',
  [ToolType.SearchSequencesTool]: 'Searching sequences',
  [ToolType.QueryWarehouseTool]: 'Querying warehouse',
  [ToolType.AttachmentTool]: 'Reading attachment',
  [ToolType.PlottingTool]: 'Creating chart',
  [ToolType.GenerateImageTool]: 'Generating image',
  [ToolType.CustomCodeTool]: 'Running code',
  [ToolType.RunCodeTool]: 'Running code',

  // Skills
  [ToolType.LoadSkill]: 'Reading skill',
  [ToolType.ManageSkill]: 'Managing skill',

  // Analysis agent
  [ToolType.CreateAnalysisPlan]: 'Creating analysis plan',

  // Data Capture
  [ToolType.Respond]: 'Finalizing response',
  [ToolType.ReadTargetDocument]: 'Reading document',
  [ToolType.ReadDraftDocument]: 'Reading document',
  [ToolType.AskQuestion]: 'Asking question',
  [ToolType.InstantiateDocument]: 'Instantiating document',
  [ToolType.SearchEntriesAndTemplates]: 'Searching entries and templates',
  [ToolType.SearchBenchlingObjects]: 'Searching Benchling objects',
  [ToolType.TranscribeFile]: 'Transcribing file',
  [ToolType.ReviewTool]: 'Reviewing',
  [ToolType.EditTableCells]: 'Editing table cells',
  [ToolType.UpdateDocument]: 'Updating document',
  [ToolType.InsertDocumentContent]: 'Inserting document content',
  [ToolType.SearchFiles]: 'Searching file content',
  [ToolType.SearchSchemas]: 'Searching schemas',
  [ToolType.FillTables]: 'Filling tables',
  [ToolType.ReadFiles]: 'Reading files',
  [ToolType.CreateLookupConfig]: 'Creating lookup configuration',

  // Co-scientist tools
  [ToolType.ListFolderContents]: 'Listing folder contents',
  [ToolType.CreateFolder]: 'Creating folder',
  [ToolType.CreateEntry]: 'Creating notebook entry',
  [ToolType.CreateFile]: 'Creating file',
  [ToolType.UpdateFile]: 'Updating file',
  [ToolType.ReadFile]: 'Reading file',
  [ToolType.CreateAASequence]: 'Creating AA sequence',
  [ToolType.CreateMolecule]: 'Creating molecule',
  [ToolType.CreateCustomEntities]: 'Creating custom entities',
  [ToolType.UpdateCustomEntities]: 'Updating custom entities',
  [ToolType.ArchiveItem]: 'Archiving item',
  [ToolType.RenameItem]: 'Renaming item',
  [ToolType.SetTargetEntry]: 'Setting target entry',
  [ToolType.CreateDocumentTool]: 'Creating document',
  [ToolType.InvokeToolsFromFile]: 'Invoking tools from file',
  [ToolType.RegisterEntities]: 'Registering entities',
  [ToolType.TransferIntoContainers]: 'Transferring into containers',
  [ToolType.CreatePlate]: 'Creating plate',
  [ToolType.StartChatSession]: 'Starting sub-agent session',
  [ToolType.GetChatSession]: 'Checking sub-agent session',
  [ToolType.ScheduleForFuture]: 'Scheduling for later',
  [ToolType.CouncilTool]: 'Thinking deeply',
  [ToolType.CreateProcedureRunPlan]: 'Creating run plan',
  [ToolType.ValidateProcedureRunPlan]: 'Checking run plan for errors',
  [ToolType.PromoteProcedureRunPlan]: 'Creating run',
  [ToolType.UpdateProcedureRunPlanConditions]: 'Updating run plan conditions',
  [ToolType.CreateProcedureRunParameterValue]: 'Recording parameter value',
};

/**
 * Human-readable display names shown after a tool has finished (past tense).
 * Tools that are not displayed in the progress UI have null values.
 */
export const TOOL_DISPLAY_NAMES_COMPLETED: Record<ToolType, string | null> = {
  // Shared
  [ToolType.ToolSearch]: 'Loaded tools',
  [ToolType.Think]: 'Thought',
  [ToolType.IdsToModelsTool]: 'Looked things up',
  [ToolType.ReadBenchlingDocumentTool]: 'Read notebook',
  [ToolType.SaveArtifact]: 'Saved artifact',
  [ToolType.ListArtifacts]: 'Listed artifacts',
  [ToolType.ViewArtifact]: 'Viewed artifact',
  [ToolType.LoadFileAsArtifact]: 'Loaded file as artifact',
  [ToolType.LoadDocumentAttachmentAsArtifact]: 'Loaded attachment as artifact',
  [ToolType.SubstituteInArtifact]: 'Edited artifact',
  [ToolType.UploadFileToSandbox]: 'Uploaded file to sandbox',
  [ToolType.LoadDropdownOptions]: 'Loaded dropdown options',
  [ToolType.HelpDocs]: 'Searched help docs',
  [ToolType.LoadPackageDocs]: 'Loaded package docs',
  [ToolType.AskWarehouse]: 'Asked warehouse',
  [ToolType.WebSearch]: 'Searched the web',

  // Deep Research
  [ToolType.ResearchSubAgent]: 'Research subagent finished',
  [ToolType.CompleteAnswer]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.CompleteAnswerTool]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.DeepResearchTool]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.PublicDataSubAgent]: 'Searched public data',
  [ToolType.SearchTool]: 'Searched Benchling',
  [ToolType.GetWarehouseOverviewTool]: 'Got warehouse overview',
  [ToolType.SearchNotebookEntries]: 'Searched notebook entries',
  [ToolType.SearchSequencesTool]: 'Searched sequences',
  [ToolType.QueryWarehouseTool]: 'Queried warehouse',
  [ToolType.AttachmentTool]: 'Read attachment',
  [ToolType.PlottingTool]: 'Created chart',
  [ToolType.GenerateImageTool]: 'Generated image',
  [ToolType.CustomCodeTool]: 'Ran code',
  [ToolType.RunCodeTool]: 'Ran code',

  // Skills
  [ToolType.LoadSkill]: 'Used Skill',
  [ToolType.ManageSkill]: 'Managed skill',

  // Analysis agent
  [ToolType.CreateAnalysisPlan]: 'Created analysis plan',

  // Data Capture
  [ToolType.Respond]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.ReadTargetDocument]: 'Read document',
  [ToolType.ReadDraftDocument]: 'Read document',
  [ToolType.AskQuestion]: 'Asked question',
  [ToolType.InstantiateDocument]: 'Instantiated document',
  [ToolType.SearchEntriesAndTemplates]: 'Searched entries and templates',
  [ToolType.SearchBenchlingObjects]: 'Searched Benchling objects',
  [ToolType.TranscribeFile]: 'Transcribed file',
  [ToolType.ReviewTool]: 'Reviewed',
  [ToolType.EditTableCells]: 'Edited table cells',
  [ToolType.UpdateDocument]: 'Updated document',
  [ToolType.InsertDocumentContent]: 'Inserted document content',
  [ToolType.SearchFiles]: 'Searched file content',
  [ToolType.SearchSchemas]: 'Searched schemas',
  [ToolType.FillTables]: 'Filled tables',
  [ToolType.ReadFiles]: 'Read files',
  [ToolType.CreateLookupConfig]: 'Created lookup configuration',

  // Co-scientist tools
  [ToolType.ListFolderContents]: 'Listed folder contents',
  [ToolType.CreateFolder]: 'Created folder',
  [ToolType.CreateEntry]: 'Created notebook entry',
  [ToolType.CreateFile]: 'Created file',
  [ToolType.UpdateFile]: 'Updated file',
  [ToolType.ReadFile]: 'Read file',
  [ToolType.CreateAASequence]: 'Created AA sequence',
  [ToolType.CreateMolecule]: 'Created molecule',
  [ToolType.CreateCustomEntities]: 'Created custom entities',
  [ToolType.UpdateCustomEntities]: 'Updated custom entities',
  [ToolType.ArchiveItem]: 'Archived item',
  [ToolType.RenameItem]: 'Renamed item',
  [ToolType.SetTargetEntry]: 'Set target entry',
  [ToolType.CreateDocumentTool]: 'Created document',
  [ToolType.InvokeToolsFromFile]: 'Invoked tools from file',
  [ToolType.RegisterEntities]: 'Registered entities',
  [ToolType.TransferIntoContainers]: 'Transferred into containers',
  [ToolType.CreatePlate]: 'Created plate',
  [ToolType.StartChatSession]: 'Started sub-agent session',
  [ToolType.GetChatSession]: 'Checked sub-agent session',
  [ToolType.ScheduleForFuture]: 'Scheduled for later',
  [ToolType.CouncilTool]: 'Thought deeply',
  [ToolType.CreateProcedureRunPlan]: 'Created run plan',
  [ToolType.ValidateProcedureRunPlan]: 'Checked run plan for errors',
  [ToolType.PromoteProcedureRunPlan]: 'Created run',
  [ToolType.UpdateProcedureRunPlanConditions]: 'Updated run plan conditions',
  [ToolType.CreateProcedureRunParameterValue]: 'Recorded parameter value',
};

// ============================================================================
// Icons
// ============================================================================

/**
 * Icons for each tool type. Null for tools that are not displayed in the progress UI.
 */
export const TOOL_ICONS: Record<ToolType, BenchlingIconName | null> = {
  // Shared
  [ToolType.ToolSearch]: 'magnifying-glass',
  [ToolType.Think]: 'lightbulb',
  [ToolType.IdsToModelsTool]: 'magnifying-glass',
  [ToolType.ReadBenchlingDocumentTool]: 'file',
  [ToolType.SaveArtifact]: 'file',
  [ToolType.ListArtifacts]: 'file',
  [ToolType.ViewArtifact]: 'file',
  [ToolType.LoadFileAsArtifact]: 'file',
  [ToolType.LoadDocumentAttachmentAsArtifact]: 'paperclip',
  [ToolType.SubstituteInArtifact]: 'edit',
  [ToolType.UploadFileToSandbox]: 'file',
  [ToolType.LoadDropdownOptions]: 'list-check',
  [ToolType.HelpDocs]: 'book',
  [ToolType.LoadPackageDocs]: 'book',
  [ToolType.AskWarehouse]: 'warehouse-full',
  [ToolType.WebSearch]: 'link',

  // Deep Research
  [ToolType.ResearchSubAgent]: 'magnifying-glass',
  [ToolType.CompleteAnswer]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.CompleteAnswerTool]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.DeepResearchTool]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.PublicDataSubAgent]: 'link',
  [ToolType.SearchTool]: 'magnifying-glass',
  [ToolType.GetWarehouseOverviewTool]: 'warehouse-full',
  [ToolType.SearchNotebookEntries]: 'magnifying-glass',
  [ToolType.SearchSequencesTool]: 'magnifying-glass',
  [ToolType.QueryWarehouseTool]: 'warehouse-full',
  [ToolType.AttachmentTool]: 'file',
  [ToolType.PlottingTool]: 'chart-bar',
  [ToolType.GenerateImageTool]: 'image',
  [ToolType.CustomCodeTool]: 'code',
  [ToolType.RunCodeTool]: 'code',

  // Skills
  [ToolType.LoadSkill]: 'file',
  [ToolType.ManageSkill]: 'edit',

  // Analysis agent
  [ToolType.CreateAnalysisPlan]: 'list-check',

  // Data Capture
  [ToolType.Respond]: null, // not in TOOLS_TO_DISPLAY
  [ToolType.ReadTargetDocument]: 'file',
  [ToolType.ReadDraftDocument]: 'file',
  [ToolType.AskQuestion]: 'magnifying-glass',
  [ToolType.InstantiateDocument]: 'file',
  [ToolType.SearchEntriesAndTemplates]: 'magnifying-glass',
  [ToolType.SearchBenchlingObjects]: 'magnifying-glass',
  [ToolType.TranscribeFile]: 'file',
  [ToolType.ReviewTool]: 'check',
  [ToolType.EditTableCells]: 'edit',
  [ToolType.UpdateDocument]: 'edit',
  [ToolType.InsertDocumentContent]: 'plus',
  [ToolType.SearchFiles]: 'magnifying-glass',
  [ToolType.SearchSchemas]: 'magnifying-glass',
  [ToolType.FillTables]: 'edit',
  [ToolType.ReadFiles]: 'file',
  [ToolType.CreateLookupConfig]: 'magnifying-glass',

  // Co-scientist tools
  [ToolType.ListFolderContents]: 'folder-open',
  [ToolType.CreateFolder]: 'folder',
  [ToolType.CreateEntry]: 'file',
  [ToolType.CreateFile]: 'file',
  [ToolType.UpdateFile]: 'edit',
  [ToolType.ReadFile]: 'file',
  [ToolType.CreateAASequence]: 'dna',
  [ToolType.CreateMolecule]: 'atom-simple',
  [ToolType.CreateCustomEntities]: 'flask',
  [ToolType.UpdateCustomEntities]: 'edit',
  [ToolType.ArchiveItem]: 'box-archive',
  [ToolType.RenameItem]: 'edit',
  [ToolType.SetTargetEntry]: 'file',
  [ToolType.CreateDocumentTool]: 'file',
  [ToolType.InvokeToolsFromFile]: 'file',
  [ToolType.RegisterEntities]: 'flask',
  [ToolType.TransferIntoContainers]: 'tube',
  [ToolType.CreatePlate]: 'well-plate',
  [ToolType.StartChatSession]: 'message-bot',
  [ToolType.GetChatSession]: 'message-bot',
  [ToolType.ScheduleForFuture]: 'clock',
  [ToolType.CouncilTool]: 'brain',
  // null intentional: these tools use the UNKNOWN_TOOL_ICON (wrench) fallback.
  [ToolType.CreateProcedureRunPlan]: null,
  [ToolType.ValidateProcedureRunPlan]: null,
  [ToolType.PromoteProcedureRunPlan]: null,
  [ToolType.UpdateProcedureRunPlanConditions]: null,
  [ToolType.CreateProcedureRunParameterValue]: null,
};

/** Icon displayed for unknown/unsupported tool types */
export const UNKNOWN_TOOL_ICON: BenchlingIconName = 'wrench';

// ============================================================================
// MCP (Model Context Protocol) Tools
// ============================================================================

/**
 * MCP tools follow the naming convention: "server_name__tool_name"
 *
 * IMPORTANT: Keep MCP_TOOL_NAME_SEPARATOR in sync with the Python constant:
 * benchling/llm/mcp_client_service/constants.py::SERVER_NAME_SEPARATOR
 */
export const MCP_TOOL_NAME_SEPARATOR = '__';

/** Default icon displayed for all MCP tools */
export const MCP_TOOL_DEFAULT_ICON: BenchlingIconName = 'plug';

/** Default description shown when an MCP tool is executing */
export const MCP_TOOL_DEFAULT_DESCRIPTION = 'Using external tool';

export function isToolType(toolName: string): toolName is ToolType {
  return Object.values(ToolType).includes(toolName as ToolType);
}

export function isMCPTool(toolName: string): boolean {
  return toolName.includes(MCP_TOOL_NAME_SEPARATOR);
}

export function parseMCPToolName(toolName: string): {serverName: string; originalToolName: string} {
  const separatorIndex = toolName.indexOf(MCP_TOOL_NAME_SEPARATOR);
  if (separatorIndex === -1) {
    return {serverName: '', originalToolName: toolName};
  }
  return {
    serverName: toolName.substring(0, separatorIndex),
    originalToolName: toolName.substring(separatorIndex + MCP_TOOL_NAME_SEPARATOR.length),
  };
}

// ============================================================================
// Display Filtering
// ============================================================================

/**
 * Tools that should be displayed in the progress UI.
 * Tools NOT in this list (e.g. Respond, ResearchSubAgent, CompleteAnswer) are
 * either handled specially or are internal implementation details.
 */
const TOOLS_TO_DISPLAY: Array<ToolType> = [
  // Shared
  ToolType.ToolSearch,
  ToolType.Think,
  ToolType.IdsToModelsTool,
  ToolType.ReadBenchlingDocumentTool,
  ToolType.SaveArtifact,
  ToolType.ListArtifacts,
  ToolType.ViewArtifact,
  ToolType.LoadFileAsArtifact,
  ToolType.LoadDocumentAttachmentAsArtifact,
  ToolType.SubstituteInArtifact,
  ToolType.UploadFileToSandbox,
  ToolType.LoadDropdownOptions,
  ToolType.HelpDocs,
  // LoadPackageDocs is intentionally omitted from TOOLS_TO_DISPLAY: it is a
  // citation source the agent calls transparently to load package usage docs
  // into context, not a user-facing progress step. It is still surfaced via
  // citations (`getAllToolCalls`), so the display name and icon above are
  // kept for that path.
  ToolType.AskWarehouse,
  ToolType.WebSearch,

  // Deep Research
  ToolType.ResearchSubAgent,
  ToolType.SearchTool,
  ToolType.SearchNotebookEntries,
  ToolType.SearchSequencesTool,
  ToolType.GetWarehouseOverviewTool,
  ToolType.QueryWarehouseTool,
  ToolType.AttachmentTool,
  ToolType.PublicDataSubAgent,
  ToolType.PlottingTool,
  ToolType.GenerateImageTool,
  ToolType.CustomCodeTool,
  ToolType.RunCodeTool,

  // Skills
  ToolType.LoadSkill,
  ToolType.ManageSkill,

  // Analysis agent
  ToolType.CreateAnalysisPlan,

  // Data Capture
  ToolType.ReadTargetDocument,
  ToolType.ReadDraftDocument,
  ToolType.AskQuestion,
  ToolType.InstantiateDocument,
  ToolType.SearchEntriesAndTemplates,
  ToolType.SearchBenchlingObjects,
  ToolType.TranscribeFile,
  ToolType.ReviewTool,
  ToolType.EditTableCells,
  ToolType.UpdateDocument,
  ToolType.InsertDocumentContent,
  ToolType.SearchFiles,
  ToolType.SearchSchemas,
  ToolType.FillTables,
  ToolType.ReadFiles,
  ToolType.CreateLookupConfig,

  // Co-scientist tools
  ToolType.ListFolderContents,
  ToolType.CreateFolder,
  ToolType.CreateEntry,
  ToolType.CreateFile,
  ToolType.UpdateFile,
  ToolType.ReadFile,
  ToolType.CreateAASequence,
  ToolType.CreateMolecule,
  ToolType.CreateCustomEntities,
  ToolType.UpdateCustomEntities,
  ToolType.ArchiveItem,
  ToolType.RenameItem,
  ToolType.SetTargetEntry,
  ToolType.CreateDocumentTool,
  ToolType.InvokeToolsFromFile,
  ToolType.RegisterEntities,
  ToolType.TransferIntoContainers,
  ToolType.CreatePlate,
  ToolType.StartChatSession,
  ToolType.GetChatSession,
  ToolType.ScheduleForFuture,
  ToolType.CouncilTool,
  ToolType.CreateProcedureRunPlan,
  ToolType.ValidateProcedureRunPlan,
  ToolType.PromoteProcedureRunPlan,
  ToolType.UpdateProcedureRunPlanConditions,
  ToolType.CreateProcedureRunParameterValue,
];

/**
 * Whether a tool should be displayed in the progress UI.
 * Returns true for tools in TOOLS_TO_DISPLAY and MCP tools.
 */
export function shouldDisplayTool(toolName: string): boolean {
  return isMCPTool(toolName) || (isToolType(toolName) && TOOLS_TO_DISPLAY.includes(toolName));
}

// ============================================================================
// Display Name Helpers
// ============================================================================

function formatExternalToolName(toolName: string): string {
  return toolName.replace(/_/g, ' ');
}

/**
 * Get the human-readable display name for a tool based on its status.
 * Optionally pass tool call args to enrich display for tools like LoadSkill.
 */
export function getToolDisplayName(
  toolName: string,
  status: ToolCallStatus,
  args?: Record<string, unknown>
): string {
  if (isMCPTool(toolName)) {
    return status === ToolCallStatus.COMPLETED ? 'Used AI Connector' : 'Using AI Connector';
  }

  const descriptions =
    status === ToolCallStatus.COMPLETED ? TOOL_DISPLAY_NAMES_COMPLETED : TOOL_DISPLAY_NAMES_IN_PROGRESS;
  const description = descriptions[toolName as ToolType];
  if (description) {
    return enrichDisplayName(toolName, description, args);
  }

  // For external tools not in the known list, show "Calling/Called {formatted name}"
  const formattedName = formatExternalToolName(toolName);
  return status === ToolCallStatus.COMPLETED ? `Called ${formattedName}` : `Calling ${formattedName}`;
}

/**
 * Extract the skill name from LoadSkill tool call arguments.
 * Returns null if not available.
 */
export function getSkillNameFromArgs(args?: Record<string, unknown>): string | null {
  if (!args) {
    return null;
  }
  const skillName = args.skill_name;
  if (typeof skillName === 'string' && skillName.length > 0) {
    return skillName;
  }
  return null;
}

export function getSkillIdFromArgs(args?: Record<string, unknown>): string | null {
  if (!args) {
    return null;
  }
  const skillId = args.skill_id;
  if (typeof skillId === 'string' && skillId.length > 0) {
    return skillId;
  }
  return null;
}

/** ToolSearch "select:" query prefix. Keep in sync with backend tool_search.py (_parse_query). */
const TOOL_SEARCH_SELECT_PREFIX = 'select:';

/**
 * For a ToolSearch "select:ToolA,ToolB" query, return the explicitly selected tool names.
 * Returns [] for keyword searches (queries that don't start with "select:") or missing args.
 */
export function getToolSearchSelectedToolNames(args?: Record<string, unknown>): Array<string> {
  const query = args?.query;
  if (typeof query !== 'string') {
    return [];
  }
  const trimmed = query.trim();
  if (!trimmed.toLowerCase().startsWith(TOOL_SEARCH_SELECT_PREFIX)) {
    return [];
  }
  return trimmed
    .slice(TOOL_SEARCH_SELECT_PREFIX.length)
    .split(',')
    .map((name) => name.trim())
    .filter((name) => name.length > 0);
}

/**
 * Enrich a base display name with tool-specific context from args.
 * LoadSkill appends ": <skill name>". ManageSkill overrides the verb
 * based on the action (create/edit).
 */
export function enrichDisplayName(
  toolName: string,
  baseName: string,
  args?: Record<string, unknown>
): string {
  if (toolName === ToolType.LoadSkill) {
    const skillId = getSkillIdFromArgs(args);
    if (skillId != null && isNativeLoadSkill(skillId)) {
      const skillName = getSkillNameFromArgs(args);
      const isCompleted = baseName === TOOL_DISPLAY_NAMES_COMPLETED[ToolType.LoadSkill];
      const contextBase = isCompleted ? 'Loaded context' : 'Loading context';
      if (skillName) {
        return `${contextBase}: ${skillName}`;
      }
      return contextBase;
    }
    const skillName = getSkillNameFromArgs(args);
    if (skillName) {
      return `${baseName}: ${skillName}`;
    }
  }
  if (toolName === ToolType.ManageSkill) {
    return getManageSkillDisplayName(baseName, args);
  }
  if (toolName === ToolType.ToolSearch) {
    // For "select:" queries, surface the chosen tool names in the title (e.g. "Loaded tools:
    // SearchSequencesTool") instead of showing the raw query as a description elsewhere.
    const selectedToolNames = getToolSearchSelectedToolNames(args);
    if (selectedToolNames.length > 0) {
      return `${baseName}: ${selectedToolNames.join(', ')}`;
    }
  }
  return baseName;
}

const MANAGE_SKILL_COMPLETED_BASE = 'Managed skill';

function getManageSkillDisplayName(baseName: string, args?: Record<string, unknown>): string {
  const action = args?.action;
  const isCompleted = baseName === MANAGE_SKILL_COMPLETED_BASE;
  if (action === 'create') {
    return isCompleted ? 'Created skill' : 'Creating skill';
  }
  if (action === 'edit') {
    return isCompleted ? 'Edited skill' : 'Editing skill';
  }
  return baseName;
}

/**
 * Get a basic display name for a tool (in-progress tense).
 * For MCP tools, returns "AI Connector: tool_name". For known tools, returns the
 * in-progress description. For unknown tools, returns the raw tool name.
 * Optionally pass tool call args to enrich display for tools like LoadSkill.
 *
 * Prefer useToolDisplayName hook for rendering, which resolves MCP server names.
 */
export function getBasicToolDisplayName(toolName: string, args?: Record<string, unknown>): string {
  if (isMCPTool(toolName)) {
    const {originalToolName} = parseMCPToolName(toolName);
    return `AI Connector: ${originalToolName}`;
  }
  const description = TOOL_DISPLAY_NAMES_IN_PROGRESS[toolName as ToolType] ?? toolName;
  return enrichDisplayName(toolName, description, args);
}

/**
 * Get the icon for a tool (handles both enum and MCP tools).
 */
export function getToolIcon(toolName: string): BenchlingIconName {
  if (isMCPTool(toolName)) {
    return MCP_TOOL_DEFAULT_ICON;
  }
  return TOOL_ICONS[toolName as ToolType] ?? UNKNOWN_TOOL_ICON;
}
